<script>
import { mapGetters, mapActions } from 'vuex'
import VueApexCharts from 'vue-apexcharts'

export default {
	name: 'CatchByFish',
	components: {
		VueApexCharts,
	},
	props: {
		userTag: {
			type: String,
		},
		isSalesman: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			categories: [],
			series: [],
			rows: [],
			total: 0,
			labelMaxLength: 20,
		}
	},
	computed: {
		...mapGetters('dashboard', ['getFishesData', 'isLoading', 'getError']),
		// таблица под графиком — от большего улова к меньшему
		sortedRows() {
			return [...this.rows].sort((a, b) => b.value - a.value)
		},
		chartOptions() {
			let $this = this
			return {
				chart: {
					type: 'bar',
					height: 520,
					toolbar: {
						show: false,
					},
				},
				plotOptions: {
					bar: {
						horizontal: false,
						borderRadius: 5,
						borderRadiusApplication: 'end',
						distributed: true,
					},
				},
				dataLabels: {
					enabled: false,
				},
				markers: {
					shape: 'circle',
				},
				// с distributed: true легенда дублирует подписи оси X и налезает на них
				legend: {
					show: false,
				},
				stroke: {
					show: true,
					width: 2,
					colors: ['transparent'],
				},
				xaxis: {
					categories: this.categories,
					labels: {
						rotate: -45,
						rotateAlways: true,
						hideOverlappingLabels: false,
						trim: false,
						maxHeight: 160,
						formatter: (val) => $this.truncateLabel(val),
						style: {
							fontSize: '12px',
						},
					},
				},
				yaxis: {
					labels: {
						formatter: function (val) {
							return $this.$options.filters.formatNumber(+val.toFixed(2))
						},
					},
				},
				fill: {
					opacity: 1,
				},
				tooltip: {
					// в подсказке показываем полное название, даже если на оси оно обрезано
					x: {
						formatter: (val, opts) => {
							const index = opts?.dataPointIndex
							return (index != null && $this.categories[index]) || val
						},
					},
					y: {
						formatter: function (val) {
							return $this.$options.filters.formatNumber(+val.toFixed(2))
						},
						title: {
							formatter: () => ``,
						},
					},
				},
			}
		},
	},
	watch: {
		getFishesData: {
			handler(newData) {
				if (newData) {
					this.parseData(newData.data || [])
					this.total = newData.total_value || 0
				}
			},
			immediate: true,
		},
	},
	mounted() {
		// Data will be loaded by the dashboard store when WellCome component initializes
	},
	methods: {
		...mapActions('dashboard', ['fetchFishesData']),
		truncateLabel(val) {
			const label = String(val ?? '')
			return label.length > this.labelMaxLength
				? label.slice(0, this.labelMaxLength - 1).trimEnd() + '…'
				: label
		},
		parseData(data) {
			this.categories = data.map((fish) => fish.fishName)
			this.series = [
				{
					data: data.map((fish) => fish.value),
				},
			]
			this.rows = data.map((fish) => ({
				name: fish.fishName,
				value: Number(fish.value) || 0,
			}))
		},
		sharePercent(row) {
			if (!this.total) return null
			return (row.value / this.total) * 100
		},
		async refreshData() {
			await this.fetchFishesData(this.userTag)
		},
	},
}
</script>

<template>
	<div
		class="bg-white/90 p-[30px] shadow-main rounded-b-[10px] backdrop-blur-[10px] overflow-y-auto max-h-[720px] scrollbar-blue"
	>
		<template>
			<div class="w-full flex justify-between items-center gap-[10px] mb-[24.18px]">
				<div class="font-main font-semibold text-[18px] text-third uppercase">
					{{ isSalesman ? $t('titles.catch_by_fish_salesman') : $t('titles.catch_by_fish') }}
				</div>
			</div>
			<div>
				<div
					class="w-full border-b border-b-solid border-b-[#D3E7FB] pb-[10px] flex items-center gap-[65px]"
				>
					<span class="font-main font-normal text-[14px] text-normal text-third">{{
						$t('headers.total')
					}}</span
					><span class="font-main font-bold text-[20px] text-main leading-[1.5]"
						>{{ total.toFixed(2) | formatNumber }} кг</span
					>
				</div>

				<!-- Show chart when there's data -->
				<template v-if="series?.length">
					<VueApexCharts
						type="bar"
						height="520"
						:options="chartOptions"
						:series="series"
					></VueApexCharts>

					<!-- Те же данные списком: на графике длинные названия обрезаны -->
					<div class="mt-[10px] overflow-x-auto">
						<table class="w-full border-collapse font-main text-[13px]">
							<thead>
								<tr class="border-b border-b-solid border-b-[#D3E7FB]">
									<th class="text-left font-semibold text-second py-[10px] pr-[10px] w-[40px]">
										№
									</th>
									<th class="text-left font-semibold text-second py-[10px] pr-[10px]">
										{{ $t('headers.fish') }}
									</th>
									<th class="text-right font-semibold text-second py-[10px] pr-[10px]">
										{{ $t('headers.volume') }}
									</th>
									<th class="text-right font-semibold text-second py-[10px] w-[90px]">
										{{ $t('headers.share') }}
									</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(row, rowInd) in sortedRows"
									:key="row.name + rowInd"
									class="border-b border-b-solid border-b-[#EEF1F6] last:border-b-0"
								>
									<td class="text-second py-[10px] pr-[10px]">{{ rowInd + 1 }}</td>
									<td class="text-third font-medium py-[10px] pr-[10px]">{{ row.name }}</td>
									<td
										class="text-third font-semibold text-right py-[10px] pr-[10px] whitespace-nowrap"
									>
										{{ row.value.toFixed(2) | formatNumber }} кг
									</td>
									<td class="text-main text-right py-[10px] whitespace-nowrap">
										{{ sharePercent(row) !== null ? sharePercent(row).toFixed(1) + ' %' : '—' }}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</template>

				<!-- Show empty state when no data -->
				<div v-else class="flex flex-col items-center justify-center h-[300px] text-center">
					<img src="@/assets/svgs/fish.svg" alt="" class="block w-16 h-16 mb-4 opacity-50" />
					<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
						{{ $t('messages.no_catch_data') }}
					</div>
					<div class="font-main font-normal text-[14px] text-second leading-[1.5]">
						{{
							$t('messages.select_fishing_period') ||
							'Выберите период рыболовства для отображения данных'
						}}
					</div>
				</div>
			</div>
		</template>
	</div>
</template>
